import { supabaseClient } from '../../services/supabase';
import { fetchCarbonIntensityData, CarbonIntensityResult } from '../../services/carbonIntensityService';
import { getBuildingsEnergyData, calculateElectricityEmissions } from '../../services/buildingsEnergyDataService';
import {
  fetchCaliforniaStateEnergyActivityFromGhgInventory,
  type CaliforniaStateEnergyActivityRow
} from '../../services/californiaGhgInventoryEnergyActivityService';
import { CALIFORNIA_STATE_GEOFIPS } from '../../services/californiaGhgInventoryVmtService';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { DataService } from '../../services/dataService';
import { fetchMergedEmfacLdvFactorsForInventory } from '../../services/emfacInventoryLdvFactors';
import { fetchAgricultureEmissionsByYearMt } from '../../services/agricultureEmissionsInventory';
import { fetchElectricityCarbonIntensityMapForGeofips } from '../../services/carbonIntensitySeriesCore';

// Extend Window interface to include vmtChartData (location key lives in vmtDataService)
declare global {
  interface Window {
    vmtChartData?: any[];
    vmtChartLocationKey?: string;
  }
}

export interface GHGInventoryResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  totalEmissions?: number;
  lightDutyEmissions?: number;
  heavyDutyEmissions?: number;
  naturalGasEmissions?: number;
  percentChange?: number;
}


// Helper function to get geofips for a location
async function getGeofipsForLocation(location: string): Promise<string | null> {
  try {
    // Use location service instead of ccas table
    await supabaseLocationService.initialize().catch(() => {});
    const geofips = await supabaseLocationService.getGeofips(location);
    
    if (geofips) {
      return String(geofips);
    }
    
    console.warn('Failed to get geofips for location:', location);
    return null; // Return null instead of defaulting to Berkeley
  } catch (error) {
    console.warn('Error getting geofips for location:', location, error);
    return null; // Return null instead of defaulting to Berkeley
  }
}


// LDV tons/mile: EMFAC2025 county filtered preferred, EMFAC2021 fills gaps (matches Policy Tool baseline).
async function fetchEMFACData(location: string): Promise<{ countyData: any[]; californiaData: any[] }> {
  const { countyData, californiaData } = await fetchMergedEmfacLdvFactorsForInventory(
    location,
    supabaseClient
  );
  console.log(
    '📊 EMFAC LDV (merged 2025→2021): county factor years:',
    countyData.length,
    'California years:',
    californiaData.length
  );
  return { countyData, californiaData };
}



async function applyVmt2022AadtBackcastIfNeeded(
  vmtData: Array<{ year: number; vmt: number; source?: string; method_note?: string }>,
  geofips?: number | null
): Promise<Array<{ year: number; vmt: number; source?: string; method_note?: string }>> {
  const vmt2022 = vmtData.find(d => d.year === 2022);
  const vmt2023 = vmtData.find(d => d.year === 2023);
  if (!geofips || (vmt2022 && vmt2022.vmt > 0) || !vmt2023 || vmt2023.vmt <= 0) {
    return vmtData;
  }

  const out = vmtData.map(d => ({ ...d }));
  console.log('⚠️ GHG Inventory: VMT for 2022 is missing or zero, backcasting from 2023 using AADT...');
  try {
    const { data: aadtData, error: aadtError } = await supabaseClient
      .from('aadt_filtered')
      .select('year, value')
      .eq('geofips', geofips)
      .eq('year', 2022)
      .single();

    if (!aadtError && aadtData && aadtData.value) {
      const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtData.value);
      console.log(
        `✅ GHG Inventory: Backcasted 2022 VMT from 2023: ${backcastedVMT2022.toLocaleString()} (2023 VMT: ${vmt2023.vmt.toLocaleString()}, AADT 2022: ${aadtData.value})`
      );

      const vmt2022Index = out.findIndex(d => d.year === 2022);
      if (vmt2022Index >= 0) {
        out[vmt2022Index].vmt = backcastedVMT2022;
        out[vmt2022Index].source = 'vmt_chart_backcasted';
        out[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
      } else {
        out.push({
          year: 2022,
          vmt: backcastedVMT2022,
          source: 'vmt_chart_backcasted',
          method_note: 'VMT data backcasted from 2023 using AADT'
        });
        out.sort((a, b) => a.year - b.year);
      }
    } else {
      console.warn('⚠️ GHG Inventory: AADT data for 2022 not found, cannot backcast:', aadtError);
    }
  } catch (error) {
    console.warn('⚠️ GHG Inventory: Error backcasting 2022 VMT:', error);
  }
  return out;
}

/**
 * Fetch VMT data for GHG Inventory
 * Preferentially uses Transportation page data (location-tagged), falls back to database if not available
 * Data is cached in global state for persistence across navigation
 */
async function fetchVMTDataForGHGInventory(location: string, geofips?: number | null): Promise<any[]> {
  console.log('🔍 GHG Inventory: Fetching VMT data for', location);
  console.log(
    '🔍 GHG Inventory: PRIORITY - VMT from Transportation chart (when it matches this location) or computed pipeline'
  );

  try {
    const { getVMTDataForGHG } = await import('../../services/vmtDataService');
    let vmtData = await getVMTDataForGHG(location, geofips || undefined);

    if (vmtData.length > 0) {
      console.log('✅ GHG Inventory: Using VMT from vmtDataService:', vmtData.length, 'records');
      vmtData = await applyVmt2022AadtBackcastIfNeeded(vmtData, geofips);
      console.log('✅ GHG Inventory: VMT data sample (first 5 years):', vmtData.slice(0, 5));
      console.log('✅ GHG Inventory: VMT data summary - Years:', vmtData.map(d => d.year).sort((a, b) => a - b));
      return vmtData;
    }

    // FALLBACK: Fetch absolute VMT by geofips only (no location name; no county fallback for absolute values).
    console.log('⚠️ GHG Inventory: No VMT Chart data found!');
    console.log('⚠️ GHG Inventory: Falling back to direct fetch by geofips only');
    
    let vmtDbData: any[] = [];
    if (geofips) {
      try {
        // LDV VMT = PRIVATE_AUTO + ON_DEMAND_AUTO (same as Transportation page); replica_vmt_by_mode has no 'total' mode
        vmtDbData = await DataService.getVMTData(undefined, ['PRIVATE_AUTO', 'ON_DEMAND_AUTO'], geofips);
        console.log('🔍 GHG Inventory: DataService by geofips returned:', vmtDbData.length, 'records');
      } catch (error) {
        console.warn('DataService.getVMTData failed:', error);
      }
    }
    // Aggregate by year: sum VMT across PRIVATE_AUTO and ON_DEMAND_AUTO per year
    const vmtByYear = new Map<number, number>();
    for (const d of vmtDbData) {
      const y = d.year;
      const v = Number(d.vmt) || 0;
      vmtByYear.set(y, (vmtByYear.get(y) ?? 0) + v);
    }
    const fallbackVmtSeries = Array.from(vmtByYear.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, vmt]) => ({
        year,
        vmt,
        source: 'database_direct',
        method_note: 'VMT data fetched directly from database (same source as Transportation page)'
      }));
    
    // Update global state with fetched data so it persists for subsequent calls
    if (fallbackVmtSeries.length > 0) {
      console.log('✅ GHG Inventory: Setting VMT data in global state for persistence');
      const { setVMTDataFromTransportation } = await import('../../services/vmtDataService');
      setVMTDataFromTransportation(fallbackVmtSeries, location);
      if (typeof window !== 'undefined') {
        window.vmtChartData = fallbackVmtSeries;
      }
    }
    
    console.log('✅ GHG Inventory: Using VMT data from database:', fallbackVmtSeries.length, 'records');
    console.log('✅ GHG Inventory: Sample VMT data:', fallbackVmtSeries.slice(0, 5));
    
    return fallbackVmtSeries;
  } catch (error) {
    console.warn('Error fetching VMT data:', error);
  return [];
  }
}



// Track in-flight requests to prevent duplicate calls
const inFlightRequests = new Map<string, Promise<GHGInventoryResult>>();

export async function fetchGHGInventoryData(locationParam: string): Promise<GHGInventoryResult> {
  // Use a different variable name to avoid conflict with DOM Location type
  const location: string = locationParam;
  console.log('🚀 GHG Inventory: Starting fetchGHGInventoryData for location:', location);
  
  // Check if there's already an in-flight request for this location
  const existingRequest = inFlightRequests.get(location);
  if (existingRequest) {
    console.log('⏸️ GHG Inventory: Request already in progress for', location, '- reusing existing request');
    return existingRequest;
  }
  
  // Create a new request and store it
  const requestPromise = (async () => {
    try {
      // Prefer Transportation page VMT edits when available, but do not require it.
      // If unavailable, fetchVMTDataForGHGInventory() will fall back to direct database fetch.
      const { isVMTDataAvailable } = await import('../../services/vmtDataService');
      if (!isVMTDataAvailable(location)) {
        // Brief wait for LocationContext/Transportation preload; then fetchVMTDataForGHGInventory will fetch VMT directly so Policy works without visiting Transport first.
        await new Promise<void>((resolve) => {
          let attempts = 0;
          const maxAttempts = 15; // ~6s; then we fetch VMT ourselves
          const intervalMs = 400;
          const checkInterval = setInterval(() => {
            attempts++;
            if (isVMTDataAvailable(location) || attempts >= maxAttempts) {
              clearInterval(checkInterval);
              resolve();
            }
          }, intervalMs);
        });
      }
      if (isVMTDataAvailable(location)) {
        console.log('✅ GHG Inventory: VMT data available from Transportation page');
      } else {
        console.log('GHG Inventory: Using fallback VMT fetch');
      }

    // Get geofips for the location to improve VMT data fetching
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
      console.log('🔍 Geofips for location:', geofips);
    } catch (error) {
      console.warn('Could not get geofips for location, will try location name:', error);
    }
    
    // Get VMT data using original GHG Inventory method (not affected by Policy Tool)
    console.log('🔍 GHG Inventory: About to call fetchVMTDataForGHGInventory');
    const vmtData = await fetchVMTDataForGHGInventory(location, geofips);
    console.log('🔍 VMT Data for GHG calculation:', vmtData);
    console.log('🔍 VMT Data length:', vmtData.length);
    console.log('🔍 VMT Data years:', vmtData.map(d => d.year));
    
    // Fetch city-specific HDV total_ghg from emfac2025_fleet
    // Note: emfac2025_fleet doesn't have total_ghg, so we calculate it from total_vmt * gco2_per_mile / 1000000
    let hdvCityGHGDataByYear: Map<number, number> = new Map();
    
    // Absolute HDV values: lookup by geofips only (no location name; avoid inconsistent spelling).
    // Do not use county fallback for absolute values—only for scaling ratios or projections elsewhere.
    let hdvDataArray: any[] = [];
    if (geofips) {
      const { data: hdv2025DataByGeofips, error: hdv2025ErrorByGeofips } = await supabaseClient
        .from('emfac2025_fleet')
        .select('year, total_vmt, gco2_per_mile')
        .eq('geofips', geofips)
        .eq('class', 'HDV')
        .gte('year', 2015)
        .lte('year', 2023)
        .order('year', { ascending: true });

      if (!hdv2025ErrorByGeofips && hdv2025DataByGeofips && hdv2025DataByGeofips.length > 0) {
        hdvDataArray = hdv2025DataByGeofips.map(row => ({
          year: row.year,
          total_ghg: row.total_vmt && row.gco2_per_mile ? (row.total_vmt * row.gco2_per_mile) / 1000000 : 0
        }));
        console.log('📊 HDV Data found using geofips:', geofips, 'count:', hdvDataArray.length);
      } else {
        console.warn('⚠️ HDV Data not found in emfac2025_fleet for geofips:', geofips, 'error:', hdv2025ErrorByGeofips);
      }
    }

    // Sum total_ghg by year (in case there are multiple rows per year for same location)
    hdvDataArray.forEach((row: any) => {
      const year = row.year;
      const totalGhg = row.total_ghg || 0;
      const currentSum = hdvCityGHGDataByYear.get(year) || 0;
      hdvCityGHGDataByYear.set(year, currentSum + totalGhg);
    });
    
    console.log('📊 HDV City GHG Data from emfac2025_fleet (before smoothing):', 
      Array.from(hdvCityGHGDataByYear.entries()).map(([year, total]) => ({ year, total_ghg: total }))
    );
    
    // Apply data smoothing to detect and fix anomalies (same logic as VMT Comparison Chart)
    // If year-over-year change > 50%, use previous year's value instead
    const sortedYears = Array.from(hdvCityGHGDataByYear.keys()).sort((a, b) => a - b);
    const smoothedHdvByYear = new Map<number, number>();
    
    sortedYears.forEach((year, index) => {
      const currentValue = hdvCityGHGDataByYear.get(year) || 0;
      
      if (index === 0) {
        // First year: use as-is
        smoothedHdvByYear.set(year, currentValue);
      } else {
        const previousYear = sortedYears[index - 1];
        const previousValue = smoothedHdvByYear.get(previousYear) || 0;
        
        // Calculate year-over-year change percentage
        const changePercent = previousValue > 0 
          ? Math.abs(currentValue - previousValue) / previousValue 
          : 1;
        
        if (changePercent > 0.5) {
          // Anomaly detected: use previous year's value
          console.warn(`⚠️ GHG Inventory: HDV ${year} anomaly detected (${(changePercent * 100).toFixed(1)}% change from ${previousYear}). Using ${previousYear} value: ${previousValue.toLocaleString()} instead of ${currentValue.toLocaleString()}`);
          smoothedHdvByYear.set(year, previousValue);
        } else {
          // Normal change: use current value
          smoothedHdvByYear.set(year, currentValue);
        }
      }
    });
    
    // Replace original map with smoothed values
    hdvCityGHGDataByYear.clear();
    smoothedHdvByYear.forEach((value, year) => {
      hdvCityGHGDataByYear.set(year, value);
    });
    
    console.log('📊 HDV City GHG Data from emfac2025_fleet (after smoothing):', 
      Array.from(hdvCityGHGDataByYear.entries()).map(([year, total]) => ({ year, total_ghg: total }))
    );
    
    // Fetch county-level HDV backcasting ratios for years 2005-2014
    await supabaseLocationService.initialize().catch(() => {});
    const countyResult = supabaseLocationService.getCountyForCity(location);
    const resolvedCounty = (typeof countyResult === 'string' ? countyResult : location).trim();
    const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
    const countyVariations = Array.from(new Set([
      `${baseCountyName} County`,
      baseCountyName,
      resolvedCounty
    ]));
    
    let hdvBackcastingRatios: { year: number; backcasting_ratio: number }[] = [];
    
    // Try location_name first - use EMFAC2025
    for (const locName of countyVariations) {
      const { data, error } = await supabaseClient
        .from('emfac2025_county_filtered')
        .select('year, backcasting_ratio')
        .eq('location_name', locName)
        .eq('class', 'HDV')
        .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
        .order('year', { ascending: true });
      if (!error && data && data.length > 0) {
        hdvBackcastingRatios = data as any;
        console.log('📊 HDV Backcasting Ratios from emfac2025_county_filtered (location_name) for', locName, ':', hdvBackcastingRatios.length, 'years');
        break;
      }
    }
    
    // Fallback: Try to get county geofips and query by geofips
    if (hdvBackcastingRatios.length === 0 && geofips) {
      try {
        // Get location info to find county
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('county, geography_type')
          .eq('geofips', geofips)
          .limit(1);
        
        if (locationData && locationData.length > 0 && locationData[0].county) {
          const countyName = locationData[0].county;
          // Try to get county geofips (typically 4-digit codes like 6001)
          const { data: countyData } = await supabaseClient
            .from('population')
            .select('geofips')
            .eq('location_name', countyName)
            .eq('geography_type', 'County')
            .limit(1);
          
          if (countyData && countyData.length > 0) {
            const countyGeofips = countyData[0].geofips;
            console.log('📊 Trying county geofips for HDV backcasting:', countyGeofips);
            const { data, error } = await supabaseClient
              .from('emfac2025_county_filtered')
              .select('year, backcasting_ratio')
              .eq('geofips', countyGeofips)
              .eq('class', 'HDV')
              .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
              .order('year', { ascending: true });
            if (!error && data && data.length > 0) {
              hdvBackcastingRatios = data as any;
              console.log('📊 HDV Backcasting Ratios from emfac2025_county_filtered (geofips) for county geofips', countyGeofips, ':', hdvBackcastingRatios.length, 'years');
            }
          }
        }
      } catch (e) {
        console.warn('📊 Error trying to get county geofips for HDV backcasting:', e);
      }
    }
    
    if (hdvBackcastingRatios.length === 0) {
      console.warn('📊 HDV Backcasting Ratios not found for any county variation');
    }
    
    const { countyData: emfacCountyData, californiaData: emfacCaliforniaData } = await fetchEMFACData(location);

    // Calculate light duty vehicle emissions (2005-2023 only)
    const lightDutyEmissions = [];
    console.log('🔍 VMT Data for GHG calculation:', vmtData);
    console.log('🔍 VMT Data length:', vmtData.length);
    console.log('🔍 VMT Data years:', vmtData.map(d => d.year));
    console.log('📊 EMFAC county emission factor data:', emfacCountyData);
    console.log('📊 EMFAC California average emission factor data:', emfacCaliforniaData);
    
    // Log emission factor data source for troubleshooting
    console.log('📊 Light-Duty Emission Factors - Data Source: emfac2025_county_filtered (preferred) + emfac2021_county_filtered (gap-fill)');
    console.log('📊 Light-Duty Emission Factors - Location:', location);
    console.log('📊 Light-Duty Emission Factors - County Data Available Years:', emfacCountyData.map(d => `${d.year}: ${d.ghg_per_mile?.toFixed(6) || 'N/A'} tons CO2/mile`));
    console.log('📊 Light-Duty Emission Factors - California Average Available Years:', emfacCaliforniaData.map(d => `${d.year}: ${d.ghg_per_mile?.toFixed(6) || 'N/A'} tons CO2/mile`));
    
    // Calculate average emission factor from county data for reference
    const validCountyEmissionFactors = emfacCountyData.filter(d => d.ghg_per_mile > 0);
    const avgCountyEmissionFactor = validCountyEmissionFactors.length > 0 
      ? validCountyEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCountyEmissionFactors.length
      : 0;
    
    // Calculate average emission factor from California data for fallback
    const validCaliforniaEmissionFactors = emfacCaliforniaData.filter(d => d.ghg_per_mile > 0);
    const avgCaliforniaEmissionFactor = validCaliforniaEmissionFactors.length > 0 
      ? validCaliforniaEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validCaliforniaEmissionFactors.length
      : 0.0008; // Final fallback emission factor (tons CO2 per mile)
    
    console.log('📊 Light-Duty Emission Factors - County Average:', avgCountyEmissionFactor > 0 ? avgCountyEmissionFactor.toFixed(6) : 'N/A', 'tons CO2/mile');
    console.log('📊 Light-Duty Emission Factors - California Average:', avgCaliforniaEmissionFactor.toFixed(6), 'tons CO2/mile');
    console.log('📊 Light-Duty Emission Factors - Final Fallback (if no data):', '0.000800 tons CO2/mile');
    
    for (let year = 2005; year <= 2023; year++) {
      const vmt = vmtData.find(d => d.year === year)?.vmt || 0;
      
      // Priority 1: Use year-specific emission factor from county data
      let yearEmissionFactorData = emfacCountyData.find(d => d.year === year);
      let emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
      let source = 'county-year-specific';
      
      // Priority 2: If county year-specific not available, use California year-specific
      if (emissionFactor === 0) {
        yearEmissionFactorData = emfacCaliforniaData.find(d => d.year === year);
        emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
        source = 'california-year-specific';
      }
      
      // Priority 3: If no year-specific data, use county average
      if (emissionFactor === 0 && avgCountyEmissionFactor > 0) {
        emissionFactor = avgCountyEmissionFactor;
        source = 'county-average';
      }
      
      // Priority 4: If no county average, use California average
      if (emissionFactor === 0) {
        emissionFactor = avgCaliforniaEmissionFactor;
        source = 'california-average';
      }
      
      // Priority 5: Final fallback
      if (emissionFactor === 0) {
        emissionFactor = 0.0008;
        source = 'final-fallback';
      }
      
      const finalEmissionFactor = emissionFactor;
      const emissions = vmt * finalEmissionFactor;
      
      // Detailed logging for troubleshooting
      console.log(`🔍 Light Duty ${year}: VMT=${vmt.toLocaleString()}, Emission Factor (${source})=${finalEmissionFactor.toFixed(6)} tons CO2/mile, Emissions=${Math.round(emissions).toLocaleString()} MT CO2e`);
      
      lightDutyEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }
    
    // Summary log of emission factors used
    console.log('📊 Light-Duty Emission Factors Summary:');
    for (let year = 2005; year <= 2023; year++) {
      let yearEmissionFactorData = emfacCountyData.find(d => d.year === year);
      let emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
      let source = '✓ county-year';
      
      if (emissionFactor === 0) {
        yearEmissionFactorData = emfacCaliforniaData.find(d => d.year === year);
        emissionFactor = yearEmissionFactorData?.ghg_per_mile || 0;
        source = '○ california-year';
      }
      
      if (emissionFactor === 0 && avgCountyEmissionFactor > 0) {
        emissionFactor = avgCountyEmissionFactor;
        source = '○ county-avg';
      }
      
      if (emissionFactor === 0) {
        emissionFactor = avgCaliforniaEmissionFactor;
        source = '○ california-avg';
      }
      
      if (emissionFactor === 0) {
        emissionFactor = 0.0008;
        source = '○ fallback';
      }
      
      console.log(`  ${year}: ${emissionFactor.toFixed(6)} tons CO2/mile (${source})`);
    }

    // Calculate heavy duty vehicle emissions
    // For 2015-2023: Use SUM of total_ghg from emfac2025_fleet (calculated from total_vmt * gco2_per_mile / 1000000)
    // For 2005-2014: Backcast using 2015 value and county-level backcasting_ratio from emfac2021_county_filtered
    const heavyDutyEmissions = [];
    
    // Get 2015 HDV emissions as baseline for backcasting
    const hdv2015 = hdvCityGHGDataByYear.get(2015) || 0;
    console.log('📊 HDV 2015 baseline for backcasting:', hdv2015, 'MT CO2e');
    
    for (let year = 2005; year <= 2023; year++) {
      let hdvEmissions = 0;
      
      if (year >= 2015) {
        // Use actual data from emfac2025_fleet (calculated from total_vmt * gco2_per_mile / 1000000)
        hdvEmissions = hdvCityGHGDataByYear.get(year) || 0;
          } else {
        // Backcast from 2015 using county-level backcasting_ratio
        const backcastRatio = hdvBackcastingRatios.find(r => r.year === year);
        if (backcastRatio && backcastRatio.backcasting_ratio && hdv2015 > 0) {
          hdvEmissions = hdv2015 * backcastRatio.backcasting_ratio;
          console.log(`📊 HDV Backcast ${year}: ratio=${backcastRatio.backcasting_ratio.toFixed(4)}, 2015 baseline=${hdv2015}, backcasted=${Math.round(hdvEmissions)}`);
        } else {
          // If no ratio available, use 2015 value as fallback
          hdvEmissions = hdv2015;
          console.log(`📊 HDV ${year}: No backcasting ratio, using 2015 baseline=${hdv2015}`);
        }
      }
      
      console.log(`📊 Heavy Duty ${year}: HDV GHG (tons)=${Math.round(hdvEmissions)}`);
      
      heavyDutyEmissions.push({
        year,
        emissions: Math.round(hdvEmissions)
      });
    }

    // Calculate natural gas emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const buildingsEnergyData = await getBuildingsEnergyData(location);
    console.log('🔍 Buildings Energy Data Debug:', {
      elecResYears: buildingsEnergyData.electricity.residential.years.length,
      elecComYears: buildingsEnergyData.electricity.commercial.years.length,
      gasResYears: buildingsEnergyData.naturalGas.residential.years.length,
      gasComYears: buildingsEnergyData.naturalGas.commercial.years.length
    });

    const naturalGasEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      // Calculate residential natural gas emissions using ONLY residential therms
      // EPA's Greenhouse Gas Equivalencies Calculator: 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      const yearIndex = buildingsEnergyData.naturalGas.residential.years.indexOf(year);
      const resTherms = buildingsEnergyData.naturalGas.residential.values[yearIndex] || 0;
      const emissions = resTherms * 0.00529; // 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      
      naturalGasEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Calculate electricity emissions using Buildings page data (2005-2023 only)
    // This includes user customizations and extrapolation from Buildings page
    const electricityEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const emissions = calculateElectricityEmissions(buildingsEnergyData, year);
      electricityEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    // Get geofips for carbon intensity lookup
    const carbonIntensityGeofips = await getGeofipsForLocation(location);
    console.log('🔍 Carbon Intensity Debug:', { location, geofips: carbonIntensityGeofips });

    // Fetch carbon intensity data for the location (only if geofips is available)
    let carbonIntensityData: CarbonIntensityResult | null = null;
    if (carbonIntensityGeofips) {
      try {
        carbonIntensityData = await fetchCarbonIntensityData(carbonIntensityGeofips);
        console.log('✅ Carbon intensity data fetched:', {
          latestValue: carbonIntensityData.latestValue,
          latestYear: carbonIntensityData.latestYear,
          dataSource: carbonIntensityData.dataSource
        });
      } catch (error) {
        console.warn('⚠️ Failed to fetch carbon intensity data, using fallback:', error);
        carbonIntensityData = null;
      }
    } else {
      console.warn('⚠️ No geofips available for carbon intensity lookup, using fallback');
    }

    const tableCiGPerKwhByYear = new Map<number, number>();
    if (carbonIntensityGeofips) {
      const gNum = Number(carbonIntensityGeofips);
      if (Number.isFinite(gNum)) {
        const m = await fetchElectricityCarbonIntensityMapForGeofips(supabaseClient, gNum, 2005, 2023);
        m.forEach((v, y) => tableCiGPerKwhByYear.set(y, v));
      }
    }

    // Use dynamic carbon intensity or fallback to 200 g CO2/kWh
    const fallbackCarbonIntensity = 0.0002; // MT CO2 per kWh (200 g CO2/kWh)
    
    // Helper function to get carbon intensity for a specific year
    const getCarbonIntensityForYear = (year: number): number => {
      const gTable = tableCiGPerKwhByYear.get(year);
      if (gTable != null && gTable > 0) {
        const intensity = gTable / 1000000;
        console.log(`🔍 Carbon intensity for ${year}: ${gTable} g CO2/kWh (electricity_carbon_intensity table) = ${intensity} MT CO2/kWh`);
        return intensity;
      }
      if (carbonIntensityData && carbonIntensityData.chartData) {
        const yearIndex = carbonIntensityData.chartData.labels.indexOf(year);
        if (yearIndex !== -1 && carbonIntensityData.chartData.datasets[0]?.data[yearIndex] !== undefined) {
          // Convert from g CO2/kWh to MT CO2/kWh
          const intensity = carbonIntensityData.chartData.datasets[0].data[yearIndex] / 1000000;
          console.log(`🔍 Carbon intensity for ${year}: ${carbonIntensityData.chartData.datasets[0].data[yearIndex]} g CO2/kWh = ${intensity} MT CO2/kWh`);
          return intensity;
        }
      }
      console.log(`🔍 Using fallback carbon intensity for ${year}: ${fallbackCarbonIntensity} MT CO2/kWh`);
      return fallbackCarbonIntensity;
    };

    // California State (geofips 6): use ghg_inventory_precalculated State row for electricity (sum of counties)
    // State row (county sums) for electricity — same basis as Energy page / upsert_ca_state_ghg.
    const useCaStateElecInventory =
      geofips != null && Number(geofips) === CALIFORNIA_STATE_GEOFIPS;
    const caElecInventoryByYear = new Map<number, CaliforniaStateEnergyActivityRow>();
    if (useCaStateElecInventory) {
      const invRows = await fetchCaliforniaStateEnergyActivityFromGhgInventory(supabaseClient);
      for (const r of invRows) {
        caElecInventoryByYear.set(r.year, r);
      }
      if (invRows.length > 0) {
        console.log(
          'GHG Inventory: California State — electricity sectors prefer ghg_inventory_precalculated State row when present.',
          { rowCount: invRows.length, firstYear: invRows[0]?.year, lastYear: invRows[invRows.length - 1]?.year }
        );
      }
    }

    // Calculate electricity emissions using Buildings page data with dynamic carbon intensity
    const electricityCommercialEmissions = [];
    const electricityResidentialEmissions = [];
    
    for (let year = 2005; year <= 2023; year++) {
      const carbonIntensity = getCarbonIntensityForYear(year);

      let resKwh =
        buildingsEnergyData.electricity.residential.values[
          buildingsEnergyData.electricity.residential.years.indexOf(year)
        ] || 0;
      let comKwh =
        buildingsEnergyData.electricity.commercial.values[
          buildingsEnergyData.electricity.commercial.years.indexOf(year)
        ] || 0;

      let resEmissions: number;
      let comEmissions: number;

      const inv = useCaStateElecInventory ? caElecInventoryByYear.get(year) : undefined;
      if (inv) {
        const invKwhSum = inv.elec_residential_kwh + inv.elec_commercial_kwh;
        const hasPrecalcEmissions =
          inv.elec_residential_emissions != null && inv.elec_commercial_emissions != null;
        if (hasPrecalcEmissions) {
          resEmissions = Math.round(inv.elec_residential_emissions as number);
          comEmissions = Math.round(inv.elec_commercial_emissions as number);
        } else if (invKwhSum > 0) {
          resKwh = inv.elec_residential_kwh;
          comKwh = inv.elec_commercial_kwh;
          resEmissions = Math.round(resKwh * carbonIntensity);
          comEmissions = Math.round(comKwh * carbonIntensity);
        } else {
          resEmissions = Math.round(resKwh * carbonIntensity);
          comEmissions = Math.round(comKwh * carbonIntensity);
        }
      } else {
        resEmissions = Math.round(resKwh * carbonIntensity);
        comEmissions = Math.round(comKwh * carbonIntensity);
      }

      electricityCommercialEmissions.push({
        year,
        emissions: comEmissions
      });

      // Debug logging for Berkeley comparison
      if (typeof location === 'string' && location.toLowerCase().includes('berkeley')) {
        const carbonIntensityGPerKwh = carbonIntensity * 1000000; // Convert back to g CO2/kWh for logging
        const roundedIntensity = Math.round(carbonIntensityGPerKwh * 100) / 100;
        const roundedKwh = Math.round(resKwh);
        const roundedEmissions = Math.round(resEmissions);
        
        // Log in a more readable format
        console.log(`🔍 Berkeley ${year} - Residential Electricity:`);
        console.log(`   kWh: ${roundedKwh.toLocaleString()}`);
        console.log(`   Carbon Intensity: ${roundedIntensity} g CO₂e/kWh (${carbonIntensity} MT CO₂e/kWh)`);
        console.log(`   Calculated Emissions: ${resEmissions.toFixed(2)} MT CO₂e`);
        console.log(`   Rounded Emissions: ${roundedEmissions} MT CO₂e`);
        console.log(`   Formula: ${roundedKwh.toLocaleString()} kWh × ${carbonIntensity} MT/kWh = ${resEmissions.toFixed(2)} MT CO₂e`);
      }
      
      electricityResidentialEmissions.push({
        year,
        emissions: resEmissions
      });
    }

    // Summary log for Berkeley comparison
    if (typeof location === 'string' && location.toLowerCase().includes('berkeley')) {
      const totalResKwh = electricityResidentialEmissions.reduce((sum, e) => {
        const yearIndex = buildingsEnergyData.electricity.residential.years.indexOf(e.year);
        return sum + (buildingsEnergyData.electricity.residential.values[yearIndex] || 0);
      }, 0);
      const totalResEmissions = electricityResidentialEmissions.reduce((sum, e) => sum + e.emissions, 0);
      const avgEmissionsPerKwh = totalResKwh > 0 ? totalResEmissions / totalResKwh : 0;
      const avgCarbonIntensity = avgEmissionsPerKwh * 1000000; // Convert to g CO2/kWh
      
      console.log(`\n📊 Berkeley Summary - Residential Electricity (2005-2023):`);
      console.log(`   Total kWh: ${Math.round(totalResKwh).toLocaleString()}`);
      console.log(`   Total Emissions: ${totalResEmissions.toFixed(2)} MT CO₂e`);
      console.log(`   Average Carbon Intensity: ${avgCarbonIntensity.toFixed(2)} g CO₂e/kWh`);
      console.log(`   Years Calculated: ${electricityResidentialEmissions.length}`);
      console.log(`   ⚠️ Compare these values with ghg_inventory_precalculated table`);
      console.log(`   Expected database values:`);
      console.log(`     - total_kwh: ~${Math.round(totalResKwh).toLocaleString()}`);
      console.log(`     - total_emissions_mt: ~${totalResEmissions.toFixed(2)}`);
      console.log(`     - avg_carbon_intensity_g_per_kwh: ~${avgCarbonIntensity.toFixed(2)}\n`);
    }

    // Calculate natural gas commercial emissions using Buildings page data
    const naturalGasCommercialEmissions = [];
    for (let year = 2005; year <= 2023; year++) {
      const comTherms = buildingsEnergyData.naturalGas.commercial.values[buildingsEnergyData.naturalGas.commercial.years.indexOf(year)] || 0;
      const emissions = comTherms * 0.00529; // EPA's Greenhouse Gas Equivalencies Calculator: 5.29 kg CO2e/therm = 0.00529 MT CO2e/therm
      
      naturalGasCommercialEmissions.push({
        year,
        emissions: Math.round(emissions)
      });
    }

    console.log('🔍 Electricity Emissions Debug:', {
      elecCom2023: electricityCommercialEmissions.find(d => d.year === 2023),
      elecRes2023: electricityResidentialEmissions.find(d => d.year === 2023),
      gasCom2023: naturalGasCommercialEmissions.find(d => d.year === 2023),
      elecComTotal: electricityCommercialEmissions.reduce((sum, d) => sum + d.emissions, 0),
      elecResTotal: electricityResidentialEmissions.reduce((sum, d) => sum + d.emissions, 0),
      gasComTotal: naturalGasCommercialEmissions.reduce((sum, d) => sum + d.emissions, 0)
    });

    // Fetch waste emissions using same calculation as GHGs chart
    // Calculate: waste_tons × emission_factor, where emission_factor = county-level SUM(ghg_emissions) / SUM(waste_tons)
    const wasteEmissions = [];
    // Store waste tons and emission factors for summary logging
    const wasteTonsByYearForLogging: Record<number, number> = {};
    const wasteEmissionFactorsByYearForLogging: Record<number, number> = {};
    if (geofips) {
      try {
        // Get location info to determine if it's a city or county (need county geofips for landfill data)
        const { data: locationData } = await supabaseClient
          .from('population')
          .select('geography_type, county')
          .eq('geofips', geofips)
          .limit(1);

        let resolvedGeofips = geofips;
        let countyGeofips = geofips;

        // If it's a city, try to get county geofips for landfill emissions data
        if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
          const countyName = locationData[0].county;
          if (countyName) {
            const { data: countyData } = await supabaseClient
              .from('population')
              .select('geofips')
              .eq('location_name', countyName)
              .eq('geography_type', 'County')
              .limit(1);

            if (countyData && countyData.length > 0) {
              countyGeofips = countyData[0].geofips;
            }
          }
        }

        // Fetch waste tons from total_waste for the selected location
        const { data: wasteData, error: wasteError } = await supabaseClient
          .from('total_waste')
          .select('year, tons_waste')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        if (wasteError) {
          console.warn('GHG Inventory: Error fetching total_waste:', wasteError);
        }

        // Fetch landfill emissions data at county level to calculate emission factors
        const { data: landfillData, error: landfillError } = await supabaseClient
          .from('landfill_emissions_full')
          .select('year, waste_tons, ghg_emissions')
          .eq('geofips', countyGeofips)
          .order('year', { ascending: true });

        if (landfillError) {
          console.warn('GHG Inventory: Error fetching landfill_emissions_full:', landfillError);
        }

        // Calculate county-specific emission factors by year
        // SUM(GHG Emissions) per county and year / SUM(waste_tons) per county and year
        const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
        (landfillData || []).forEach((r: any) => {
          const year = r.year;
          if (!countyTotalsByYear[year]) {
            countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
          }
          countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
          countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
        });

        // Calculate emission factor (carbon intensity) for each year
        const emissionFactorsByYear: Record<number, number> = {};
        Object.keys(countyTotalsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          const totals = countyTotalsByYear[year];
          if (totals.totalWaste > 0) {
            emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
          }
        });

        // Check if we have valid county emission factors, if not fetch statewide average by aggregating all California counties
        const hasValidCountyFactors = Object.keys(emissionFactorsByYear).length > 0 && 
          Object.values(emissionFactorsByYear).some(factor => factor > 0);
        
        if (!hasValidCountyFactors) {
          console.log('GHG Inventory: No valid county emission factors, calculating statewide average from all California counties');
          
          // Fetch all California county data (geofips starting with 60, typically 4-digit county codes)
          // This aggregates all counties to get California-wide averages
          const { data: californiaLandfillData, error: californiaError } = await supabaseClient
            .from('landfill_emissions_full')
            .select('year, waste_tons, ghg_emissions, geofips')
            .gte('geofips', 6000)
            .lt('geofips', 7000) // California counties are typically 6000-6999
            .order('year', { ascending: true });

          if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
            // Calculate statewide emission factors
            const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
            californiaLandfillData.forEach((r: any) => {
              const year = r.year;
              if (!stateTotalsByYear[year]) {
                stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
              }
              stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
              stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
            });

            // Calculate statewide emission factors
            Object.keys(stateTotalsByYear).forEach(yearStr => {
              const year = parseInt(yearStr);
              const totals = stateTotalsByYear[year];
              if (totals.totalWaste > 0) {
                emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
              }
            });
            
            console.log('GHG Inventory: Using statewide average emission factors');
          } else {
            console.warn('GHG Inventory: Error fetching California statewide emission factors:', californiaError);
          }
        } else {
          // If we have some county factors but not all years, fill missing years with statewide average
          const allRequiredYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
          const missingYears = allRequiredYears.filter(year => !emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0);
          
          if (missingYears.length > 0) {
            console.log(`GHG Inventory: Filling ${missingYears.length} missing years with statewide average`);
            
            // Fetch all California county data to calculate statewide averages
            const { data: californiaLandfillData, error: californiaError } = await supabaseClient
              .from('landfill_emissions_full')
              .select('year, waste_tons, ghg_emissions, geofips')
              .gte('geofips', 6000)
              .lt('geofips', 7000) // California counties are typically 6000-6999
              .order('year', { ascending: true });

            if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
              // Calculate statewide emission factors for missing years
              const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
              californiaLandfillData.forEach((r: any) => {
                const year = r.year;
                if (missingYears.includes(year)) {
                  if (!stateTotalsByYear[year]) {
                    stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
                  }
                  stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
                  stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
                }
              });

              // Fill in missing years with statewide factors
              Object.keys(stateTotalsByYear).forEach(yearStr => {
                const year = parseInt(yearStr);
                const totals = stateTotalsByYear[year];
                if (totals.totalWaste > 0 && (!emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0)) {
                  emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
                }
              });
            }
          }
        }

        // Project carbon intensity linearly for years 2020-2023 if data not available
        const yearsWithFactors = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        if (yearsWithFactors.length > 0) {
          const latestYearWithFactor = Math.max(...yearsWithFactors);
          const lastYear = latestYearWithFactor;
          const lastFactor = emissionFactorsByYear[lastYear];
          
          // If latest year is before 2023, project forward
          if (latestYearWithFactor < 2023) {
            // Find the last two years with data for linear projection
            const secondLastYear = yearsWithFactors[yearsWithFactors.length - 2];
            
            if (secondLastYear && secondLastYear < lastYear) {
              const secondLastFactor = emissionFactorsByYear[secondLastYear];
              
              // Calculate trend per year
              const yearsDiff = lastYear - secondLastYear;
              const factorDiff = lastFactor - secondLastFactor;
              const trendPerYear = factorDiff / yearsDiff;
              
              // Project for years 2020-2023 if missing
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  const yearsToProject = year - lastYear;
                  emissionFactorsByYear[year] = lastFactor + (trendPerYear * yearsToProject);
                }
              }
            } else {
              // If only one year of data, use that value for all missing years
              for (let year = 2020; year <= 2023; year++) {
                if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
                  emissionFactorsByYear[year] = lastFactor;
                }
              }
            }
          }
        }

        // Fetch population data for backcasting waste tons
        const { data: populationData } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('geofips', resolvedGeofips)
          .order('year', { ascending: true });

        // Build population by year
        const populationByYear: Record<number, number> = {};
        (populationData || []).forEach((r: any) => {
          populationByYear[r.year] = Number(r.population || 0);
        });

        // Aggregate waste tons by year for the selected location
        const wasteTonsByYear: Record<number, number> = {};
        (wasteData || []).forEach((r: any) => {
          const year = r.year;
          wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
        });

        // Backcast waste tons to 2005 using population ratios
        const wasteYears = Object.keys(wasteTonsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

        if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
          const baseTons = wasteTonsByYear[earliestWasteYear];
          const basePopulation = populationByYear[earliestWasteYear];

          // Backcast years before earliest waste data year
          for (let year = 2005; year < earliestWasteYear; year++) {
            const yearPopulation = populationByYear[year] || basePopulation;
            const populationRatio = yearPopulation / basePopulation;
            
            // Backcast tons using population ratio
            wasteTonsByYear[year] = baseTons * populationRatio;
          }
        }

        // Backcast emission factors to 2005 if needed
        const factorYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        const earliestFactorYear = factorYears.length > 0 ? Math.min(...factorYears) : null;

        if (earliestFactorYear && earliestFactorYear > 2005) {
          const baseFactor = emissionFactorsByYear[earliestFactorYear];
          
          // Use earliest factor for all years before earliest data year
          for (let year = 2005; year < earliestFactorYear; year++) {
            emissionFactorsByYear[year] = baseFactor;
          }
        }

        // Calculate GHG emissions = waste_tons × emission_factor for each year (2005-2023)
        const calculatedEmissionsByYear: Record<number, number> = {};
        const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
        
        allYears.forEach(year => {
          const wasteTons = wasteTonsByYear[year] || 0;
          const emissionFactor = emissionFactorsByYear[year] || 0;
          
          if (wasteTons > 0 && emissionFactor > 0) {
            calculatedEmissionsByYear[year] = wasteTons * emissionFactor;
          } else {
            calculatedEmissionsByYear[year] = 0;
          }
        });
        
        // Store waste tons and emission factors for summary logging
        Object.keys(wasteTonsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          wasteTonsByYearForLogging[year] = wasteTonsByYear[year];
        });
        Object.keys(emissionFactorsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          wasteEmissionFactorsByYearForLogging[year] = emissionFactorsByYear[year];
        });
        
        // Create waste emissions array for all years 2005-2023
    for (let year = 2005; year <= 2023; year++) {
          const emissions = calculatedEmissionsByYear[year] || 0;
          wasteEmissions.push({ year, category: 'Waste', emissions: Math.round(emissions) });
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch waste emissions data, using zeros:', error);
        // Fill with zeros if fetch fails
        for (let year = 2005; year <= 2023; year++) {
          wasteEmissions.push({ year, category: 'Waste', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        wasteEmissions.push({ year, category: 'Waste', emissions: 0 });
      }
    }

    // Fetch Offroad emissions from offroad_emissions table using exact geofips match only.
    // IMPORTANT: Respect the selected geography type:
    // - City selections should use geography_type = 'place'
    // - County selections should use geography_type = 'county'
    // - State selections should use geography_type = 'state'
    const offroadEmissions = [];
    if (geofips) {
      try {
        // Convert geofips to string (no leading zeros - table stores as TEXT without leading zeros)
        const geofipsStr = String(geofips);

        // Determine the selected geography_type from the population table (year 2023 is sufficient)
        // Population uses 'City' while offroad_emissions uses 'place'
        let offroadGeographyType: string | null = null;
        try {
          const { data: popRow, error: popErr } = await supabaseClient
            .from('population')
            .select('geography_type')
            .eq('geofips', geofips)
            .eq('year', 2023)
            .limit(1);
          if (popErr) {
            console.warn('GHG Inventory: Error fetching population geography_type:', popErr);
          }
          const popType = popRow?.[0]?.geography_type;
          if (popType) {
            if (popType === 'City') offroadGeographyType = 'place';
            else if (popType === 'County') offroadGeographyType = 'county';
            else if (popType === 'State') offroadGeographyType = 'state';
            else offroadGeographyType = String(popType).toLowerCase();
          }
        } catch (e) {
          console.warn('GHG Inventory: Failed to resolve population geography_type for offroad:', e);
        }
        if (!offroadGeographyType) {
          // Fallback heuristic: cities are typically 6 digits, counties 4, state is 1
          const len = geofipsStr.length;
          offroadGeographyType = len >= 6 ? 'place' : len <= 2 ? 'state' : 'county';
        }
        
        // Fetch offroad emissions data for exact geofips only (no county fallback)
        // Note: Table uses geofips (lowercase) and ghg_emissions_tco2e (not emissions)
        // Sum by year since there are multiple categories per year
        const { data: offroadData, error: offroadError } = await supabaseClient
          .from('offroad_emissions')
          .select('year, ghg_emissions_tco2e')
          .eq('geofips', geofipsStr)
          .eq('geography_type', offroadGeographyType)
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });

        if (offroadError) {
          console.warn('GHG Inventory: Error fetching offroad_emissions:', offroadError);
        }

        // Map emissions by year, summing across all categories for each year
        const offroadEmissionsByYear: Record<number, number> = {};
        (offroadData || []).forEach((r: any) => {
          const year = r.year;
          const emissions = Number(r.ghg_emissions_tco2e || 0);
          // Sum emissions across all categories for the same year
          offroadEmissionsByYear[year] = (offroadEmissionsByYear[year] || 0) + emissions;
        });

        // Create offroad emissions array for all years 2005-2023
        // If no data found, values will be zero
        for (let year = 2005; year <= 2023; year++) {
          const emissions = offroadEmissionsByYear[year] || 0;
          offroadEmissions.push({ year, category: 'Offroad', emissions: Math.round(emissions) });
        }

        console.log('📊 Offroad Emissions:', {
          geofips,
          dataPoints: offroadData?.length || 0,
          yearsWithData: Object.keys(offroadEmissionsByYear).length,
          sampleYears: Object.keys(offroadEmissionsByYear).slice(0, 5),
          sampleEmissions: Object.keys(offroadEmissionsByYear).slice(0, 3).map(y => ({
            year: parseInt(y),
            emissions: offroadEmissionsByYear[parseInt(y)]
          }))
        });
      } catch (error) {
        console.warn('⚠️ Failed to fetch offroad emissions data, using zeros:', error);
        // Fill with zeros if fetch fails
        for (let year = 2005; year <= 2023; year++) {
          offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
        }
      }
    } else {
      // Fill with zeros if no geofips
      for (let year = 2005; year <= 2023; year++) {
        offroadEmissions.push({ year, category: 'Offroad', emissions: 0 });
      }
    }

    /** County-only agricultural GHG (ag_emissions); cities get 0 — same as Waste+ Agriculture chart scope */
    const agricultureEmissions: { year: number; category: string; emissions: number }[] = [];
    try {
      if (geofips) {
        const { data: popGeo } = await supabaseClient
          .from('population')
          .select('geography_type')
          .eq('geofips', geofips)
          .limit(1);
        const geoT = popGeo?.[0]?.geography_type;
        const agByYear = await fetchAgricultureEmissionsByYearMt(supabaseClient, String(geofips), geoT);
        for (let year = 2005; year <= 2023; year++) {
          agricultureEmissions.push({
            year,
            category: 'Agriculture',
            emissions: Math.round(agByYear[year] || 0),
          });
        }
      } else {
        for (let year = 2005; year <= 2023; year++) {
          agricultureEmissions.push({ year, category: 'Agriculture', emissions: 0 });
        }
      }
    } catch (e) {
      console.warn('GHG Inventory: agriculture emissions failed, using zeros:', e);
      for (let year = 2005; year <= 2023; year++) {
        agricultureEmissions.push({ year, category: 'Agriculture', emissions: 0 });
      }
    }

    // Combine all data
    const allEmissions = [
      ...lightDutyEmissions.map(d => ({ ...d, category: 'Light Duty Vehicles' })),
      ...heavyDutyEmissions.map(d => ({ ...d, category: 'Heavy Duty Vehicles' })),
      ...naturalGasEmissions.map(d => ({ ...d, category: 'Natural Gas Residential' })),
      ...electricityCommercialEmissions.map(d => ({ ...d, category: 'Electricity Commercial' })),
      ...electricityResidentialEmissions.map(d => ({ ...d, category: 'Electricity Residential' })),
      ...naturalGasCommercialEmissions.map(d => ({ ...d, category: 'Natural Gas Commercial' })),
      ...wasteEmissions,
      ...offroadEmissions,
      ...agricultureEmissions,
    ];
    
    console.log('🔍 2023 Data Breakdown:', {
      lightDuty2023: allEmissions.find(d => d.category === 'Light Duty Vehicles' && d.year === 2023),
      heavyDuty2023: allEmissions.find(d => d.category === 'Heavy Duty Vehicles' && d.year === 2023),
      naturalGasRes2023: allEmissions.find(d => d.category === 'Natural Gas Residential' && d.year === 2023),
      elecCom2023: allEmissions.find(d => d.category === 'Electricity Commercial' && d.year === 2023),
      elecRes2023: allEmissions.find(d => d.category === 'Electricity Residential' && d.year === 2023),
      naturalGasCom2023: allEmissions.find(d => d.category === 'Natural Gas Commercial' && d.year === 2023),
      waste2023: allEmissions.find(d => d.category === 'Waste' && d.year === 2023),
      offroad2023: allEmissions.find(d => d.category === 'Offroad' && d.year === 2023),
      agriculture2023: allEmissions.find(d => d.category === 'Agriculture' && d.year === 2023),
      total2023: allEmissions.filter(d => d.year === 2023).reduce((sum, d) => sum + d.emissions, 0)
    });
    
    // Debug: Check VMT data
    console.log('VMT Data:', vmtData);
    console.log('VMT years with data:', vmtData.filter(d => d.vmt > 0).map(d => d.year));
    
    // Debug: Check all Heavy Duty data
    const heavyDutyEmissionsData = allEmissions.filter(d => d.category === 'Heavy Duty Vehicles');
    console.log('All Heavy Duty data:', heavyDutyEmissionsData);
    console.log('Heavy Duty years:', heavyDutyEmissionsData.map(d => d.year));
    console.log('Heavy Duty 2023 specifically:', heavyDutyEmissionsData.find(d => d.year === 2023));

    // Get unique years and categories
    const years = Array.from(new Set(allEmissions.map(d => d.year))).sort((a, b) => a - b);
    const categories = Array.from(new Set(allEmissions.map(d => d.category)));

    // Create datasets for each category
    const datasets = categories.map((category, index) => {
      // Color mapping based on category name - using shades of same color for related sources
      const getColorForCategory = (cat: string): { bg: string, border: string } => {
        switch (cat) {
          case 'Light Duty Vehicles':
            return { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' };  // Transportation - darker blue
          case 'Heavy Duty Vehicles':
            return { bg: 'rgba(96, 165, 250, 0.8)', border: 'rgba(96, 165, 250, 1)' };  // Transportation - lighter blue
          case 'Natural Gas Residential':
            return { bg: 'rgba(245, 158, 11, 0.8)', border: 'rgba(245, 158, 11, 1)' };  // Natural Gas - darker amber
          case 'Natural Gas Commercial':
            return { bg: 'rgba(251, 191, 36, 0.8)', border: 'rgba(251, 191, 36, 1)' };  // Natural Gas - lighter amber
          case 'Electricity Residential':
            return { bg: 'rgba(16, 185, 129, 0.8)', border: 'rgba(16, 185, 129, 1)' };  // Electricity - darker emerald
          case 'Electricity Commercial':
            return { bg: 'rgba(52, 211, 153, 0.8)', border: 'rgba(52, 211, 153, 1)' };  // Electricity - lighter emerald
          case 'Waste':
            return { bg: 'rgba(236, 72, 153, 0.8)', border: 'rgba(236, 72, 153, 1)' };  // Pink for waste
          case 'Offroad':
            return { bg: 'rgba(255, 210, 0, 0.8)', border: 'rgba(255, 210, 0, 1)' };  // UC Gold for offroad
          case 'Agriculture':
            return { bg: 'rgba(22, 101, 52, 0.85)', border: 'rgba(22, 101, 52, 1)' }; // County ag (ag_emissions)
          default:
            return { bg: 'rgba(100, 116, 139, 0.8)', border: 'rgba(100, 116, 139, 1)' };
        }
      };
      
      const colors = getColorForCategory(category);
      const color = colors.bg;
      const borderColor = colors.border;

      const data = years.map(year => {
        const entry = allEmissions.find(d => d.category === category && d.year === year);
        const value = entry ? entry.emissions : 0;
        if (category === 'Heavy Duty Vehicles' && year === 2023) {
          console.log(`Chart data for Heavy Duty 2023: entry=`, entry, 'value=', value);
        }
        return value;
      });

      console.log(`Dataset for ${category}:`, {
        label: category,
        data: data.slice(-5), // Show last 5 years
        years: years.slice(-5),
        fullData: data // Show all data for debugging
      });

      return {
        label: category,
        data,
        backgroundColor: color,
        borderColor,
        borderWidth: 2
      };
    });

    const chartData = {
      labels: years,
      datasets
    };

    console.log('🔍 Chart Data Debug:', {
      yearsCount: years.length,
      years: years,
      datasetsCount: datasets.length,
      firstDatasetData: datasets[0]?.data,
      firstDatasetDataLength: datasets[0]?.data?.length,
      lastDatasetData: datasets[datasets.length - 1]?.data,
      lastDatasetDataLength: datasets[datasets.length - 1]?.data?.length
    });

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: `GHG Inventory - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Emissions (Metric Tons CO₂e)'
          },
          ticks: {
            callback: (value: any) => Number(value).toLocaleString()
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };

    // Calculate metrics
    const latestYear = years[years.length - 1];
    const latestYearData = allEmissions.filter(d => d.year === latestYear);
    const totalEmissions = latestYearData.reduce((sum, d) => sum + d.emissions, 0);
    const lightDutyLatest = latestYearData.find(d => d.category === 'Light Duty Vehicles')?.emissions || 0;
    const heavyDutyLatest = latestYearData.find(d => d.category === 'Heavy Duty Vehicles')?.emissions || 0;
    const naturalGasLatest = latestYearData.find(d => d.category === 'Natural Gas Residential')?.emissions || 0;

    // Calculate percent change from 2005
    const emissions2005 = allEmissions.filter(d => d.year === 2005).reduce((sum, d) => sum + d.emissions, 0);
    const percentChange = emissions2005 > 0 ? ((totalEmissions - emissions2005) / emissions2005) * 100 : 0;

      return {
        chartData,
        chartOptions,
        latestYear,
        totalEmissions,
        lightDutyEmissions: lightDutyLatest,
        heavyDutyEmissions: heavyDutyLatest,
        naturalGasEmissions: naturalGasLatest,
        percentChange
      };
    } catch (error) {
      console.error('Error fetching GHG inventory data:', error);
      throw error;
    } finally {
      // Remove the request from the map when done (success or failure)
      inFlightRequests.delete(location);
    }
  })();
  
  // Store the promise in the map
  inFlightRequests.set(location, requestPromise);
  
  return requestPromise;
}
